import { gql } from 'apollo'
import { sortBy } from 'lodash-es'
import { IDropdownOption } from 'office-ui-fabric-react'
import * as React from 'react'
import { useApolloClient } from 'react-apollo-hooks'
import { useTranslation } from 'react-i18next'
import {
  AsyncDropdown,
  AsyncDropdownProps,
  FormikAsyncDropdownFactory,
} from '../AsyncDropdown'
import { ResourceDropdownQuery } from './typings/ResourceDropdownQuery'

export const RESOURCE_DROPDOWN_QUERY = gql`
  query ResourceDropdownQuery(
    $rootId: String!
    $category: String!
    $type: String
  ) {
    resources(
      filter: {
        rootId: { EQ: $rootId }
        category: { EQ: $category }
        type: { EQ: $type }
      }
    ) {
      _id
      type
      category
      title
    }
  }
`

export type ResourceDropdownProps = Omit<
  AsyncDropdownProps,
  'onResolveOptions'
> & {
  rootId: string
  category: string
  excludeTypes?: string[]
  type?: string
  onlyIncludeType?: string
}

export const ResourceDropdown: React.FunctionComponent<
  ResourceDropdownProps
> = ({ type, category, excludeTypes, rootId, onlyIncludeType, ...props }) => {
  const { t } = useTranslation()
  const client = useApolloClient()

  const resolveOptions = React.useMemo<
    PromiseLike<IDropdownOption[]>
  >(async () => {
    const { data } = await client.query<ResourceDropdownQuery>({
      query: RESOURCE_DROPDOWN_QUERY,
      variables: { type, category, rootId },
      fetchResults: true,
    })
    const typesToExclude = excludeTypes || []

    const options: IDropdownOption[] = data.resources
      .filter(
        (x) =>
          !typesToExclude.includes(x.type) &&
          (onlyIncludeType ? x.type === onlyIncludeType : true)
      )
      .map((x) => ({
        key: x._id,
        text: type
          ? `${x.title}`
          : `${x.title} (${t(`field.type.${x.type}`, {
              category: t(`field.category.${x.category}`),
            })})`,
      }))

    return sortBy(options, 'text')
  }, [client, t, type, category, rootId, excludeTypes, onlyIncludeType])

  // TODO: i18n
  const placeholders = React.useMemo(
    () => ({
      empty: `Keine ${
        type
          ? t(`field.type.${type}`, {
              category: t(`field.category.${category}`),
            })
          : t(`field.category.${category}`)
      } vorhanden`,
    }),
    [t, type, category]
  )

  return (
    <AsyncDropdown
      onResolveOptions={resolveOptions}
      placeholders={placeholders}
      {...props}
    />
  )
}

export const FormikResourceDropdown =
  FormikAsyncDropdownFactory(ResourceDropdown)
